import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { setFonemaIncomingRingingBusy } from '../../../../services/fonemaCallBusyService';
import { mapVoiceIncomingToCallInfoData } from '../../calls/callInfoDataMapper';
import useWhatsappCallDefaultFrom from '../../calls/useWhatsappCallDefaultFrom';
import {
  voiceRemoveIncomingCall,
  voiceClearActiveCall
} from '../../../../actions/voice/voiceActions';
import {
  clearVoiceIncomingCallAnsweredHandler,
  setVoiceIncomingCallAnsweredHandler
} from './voiceIncomingCallAnsweredNotifier';

const selectVoiceBridgeState = (state) => ({
  incomingCalls: state.voiceReducer?.incomingCalls ?? [],
  activeCall: state.voiceReducer?.activeCall ?? null,
  isMediaConnected: Boolean(state.voiceReducer?.isConnected),
  isSocketConnected: Boolean(state.voiceReducer?.isSocketConnected)
});

const noop = () => {};

/**
 * Fonema-compatible audio state for Voice Platform (mount with VoiceRuntimeBoot).
 *
 * @param {{ callUiEvents?: ReturnType<typeof import('../../calls/callUiEvents').default> | null }} [options]
 */
export default function useVoiceWhatsappAudioBridge(options = {}) {
  const { callUiEvents = null } = options;
  const dispatch = useDispatch();
  const defaultFrom = useWhatsappCallDefaultFrom();
  const {
    incomingCalls,
    activeCall,
    isMediaConnected,
    isSocketConnected
  } = useSelector(selectVoiceBridgeState, shallowEqual);

  const [callEnrichments, setCallEnrichments] = useState({});
  const incomingCallsRef = useRef(incomingCalls);
  incomingCallsRef.current = incomingCalls;

  const incomingPhones = useMemo(
    () => incomingCalls.map((call) => ({
      ...mapVoiceIncomingToCallInfoData(call, defaultFrom),
      ...(callEnrichments[call.callControlId] ?? {})
    })),
    [incomingCalls, defaultFrom, callEnrichments]
  );

  const incomingCount = incomingCalls.length;

  useEffect(() => {
    setFonemaIncomingRingingBusy(incomingCount > 0);
  }, [incomingCount]);

  useEffect(() => () => setFonemaIncomingRingingBusy(false), []);

  const clearCallReferences = useCallback(() => {
    dispatch(voiceClearActiveCall());
  }, [dispatch]);

  const handleIncomingCallAnswered = useCallback(() => {
    window.stopRingtone?.();
  }, []);

  useEffect(() => {
    setVoiceIncomingCallAnsweredHandler(handleIncomingCallAnswered);
    callUiEvents?.registerStopRingtone(handleIncomingCallAnswered);
    callUiEvents?.registerClearCallReferences(clearCallReferences);
    return () => {
      clearVoiceIncomingCallAnsweredHandler();
      callUiEvents?.registerStopRingtone(null);
      callUiEvents?.registerClearCallReferences(null);
    };
  }, [callUiEvents, clearCallReferences, handleIncomingCallAnswered]);

  const removeIncomingPhone = useCallback((callId) => {
    dispatch(voiceRemoveIncomingCall(callId));
    setCallEnrichments((prev) => {
      if (!prev[callId]) return prev;
      const next = { ...prev };
      delete next[callId];
      return next;
    });
  }, [dispatch]);

  const updateIncomingPhone = useCallback((callId, updatesOrUpdater) => {
    const voiceCall = incomingCallsRef.current.find((call) => call.callControlId === callId);
    if (!voiceCall) return false;

    setCallEnrichments((prev) => {
      const current = {
        ...mapVoiceIncomingToCallInfoData(voiceCall, defaultFrom),
        ...(prev[callId] ?? {})
      };
      const nextEntry = typeof updatesOrUpdater === 'function'
        ? updatesOrUpdater(current)
        : { ...current, ...(updatesOrUpdater ?? {}) };
      return { ...prev, [callId]: nextEntry };
    });

    return true;
  }, [defaultFrom]);

  const sdkReady = isMediaConnected || isSocketConnected || incomingCalls.length > 0;

  return useMemo(() => ({
    incomingPhones,
    removeIncomingPhone,
    updateIncomingPhone,
    clearCallReferences,
    sdkReady,
    from: defaultFrom,
    setIncomingPhones: noop,
    setIsAvailable: noop,
    setFrom: noop,
    isAvailable: !activeCall
  }), [
    incomingPhones,
    removeIncomingPhone,
    updateIncomingPhone,
    clearCallReferences,
    sdkReady,
    defaultFrom,
    activeCall
  ]);
}
